(() => {
  'use strict';

  const attributionStorageKey = 'avt-journey-attribution-v1';
  const attributionParameters = Object.freeze({
    utm_source: 'attribution_source',
    utm_medium: 'attribution_medium',
    utm_campaign: 'attribution_campaign',
  });
  const guideActions = Object.freeze(['project', 'builder', 'shop', 'help']);
  const handoffStages = Object.freeze(['opened', 'submitted']);
  const audioBuilderStages = Object.freeze([
    'source', 'speakers', 'power', 'subwoofer', 'mounting', 'electrical', 'review',
  ]);

  const sanitizeSlug = (value, maxLength = 80) => {
    const text = String(value ?? '').trim();
    if (
      !text
      || text.length > maxLength
      || !/^[a-z0-9][a-z0-9._-]*$/i.test(text)
    ) {
      return undefined;
    }
    return text;
  };

  const sanitizePositiveInteger = (value, maximum = 1000000) => {
    const number = Number(value);
    return Number.isInteger(number) && number >= 1 && number <= maximum
      ? number
      : undefined;
  };

  const sanitizeNonnegativeInteger = (value, maximum = 1000000) => {
    if (value === '' || value === null || value === undefined) return undefined;
    const number = Number(value);
    return Number.isInteger(number) && number >= 0 && number <= maximum
      ? number
      : undefined;
  };

  const sanitizeShopifyId = (value) => {
    const text = String(value ?? '').trim();
    return /^\d{1,20}$/.test(text) ? text : undefined;
  };

  const sanitizeBikeKey = (value) => {
    const text = String(value ?? '').trim();
    const [make, model, yearText, extra] = text.split('::');
    const motorcycleText = /^[A-Za-z0-9][A-Za-z0-9&().,'+/\- ]{0,79}$/;
    const year = Number(yearText);
    if (
      extra !== undefined
      || !motorcycleText.test(make || '')
      || !motorcycleText.test(model || '')
      || !Number.isInteger(year)
      || year < 1900
      || year > 2200
    ) {
      return undefined;
    }
    return `${make}::${model}::${year}`;
  };

  const sanitizeEnum = (value, allowed) => {
    const text = String(value ?? '').trim();
    return allowed.includes(text) ? text : undefined;
  };

  const sanitizeAttribution = (value) => {
    const raw = String(value ?? '').trim();
    if (
      !raw
      || raw.length > 240
      || raw.includes('@')
      || /(?:\d[\s()+.-]*){7,}/.test(raw)
      || /^[A-HJ-NPR-Z0-9]{17}$/i.test(raw.replace(/[^A-Z0-9]/gi, ''))
    ) {
      return undefined;
    }
    const sanitized = raw
      .normalize('NFKC')
      .replace(/[^A-Za-z0-9._~:/+-]+/g, '-')
      .replace(/-{2,}/g, '-')
      .replace(/^[-.]+|[-.]+$/g, '')
      .slice(0, 80);
    return sanitized || undefined;
  };

  const fieldSanitizers = Object.freeze({
    action: (value) => sanitizeEnum(value, guideActions),
    article_handle: (value) => sanitizeSlug(value, 120),
    bike_key: sanitizeBikeKey,
    builder_stage: (value) => sanitizeEnum(value, audioBuilderStages),
    blocking_gap_count: (value) => sanitizeNonnegativeInteger(value, 1000),
    intent: (value) => sanitizeSlug(value, 80),
    item_count: (value) => sanitizePositiveInteger(value, 1000),
    job_id: (value) => sanitizeSlug(value, 120),
    job_version: (value) => sanitizePositiveInteger(value, 100000),
    match_count: (value) => sanitizeNonnegativeInteger(value, 1000),
    product_id: sanitizeShopifyId,
    request: (value) => sanitizeSlug(value, 80),
    source: (value) => sanitizeSlug(value, 80),
    stage: (value) => sanitizeEnum(value, handoffStages),
    unresolved_role_count: (value) => sanitizeNonnegativeInteger(value, 1000),
    variant_id: sanitizeShopifyId,
    warning_count: (value) => sanitizeNonnegativeInteger(value, 1000),
  });

  const eventSchemas = Object.freeze({
    'avt:bike_saved': Object.freeze({
      required: Object.freeze(['bike_key', 'source']),
      fields: Object.freeze(['bike_key', 'source']),
    }),
    'avt:audio_builder_result': Object.freeze({
      required: Object.freeze(['bike_key', 'match_count', 'source']),
      fields: Object.freeze(['bike_key', 'match_count', 'source']),
    }),
    'avt:audio_builder_started': Object.freeze({
      required: Object.freeze(['source']),
      fields: Object.freeze(['source']),
    }),
    'avt:audio_bike_selected': Object.freeze({
      required: Object.freeze(['bike_key', 'source']),
      fields: Object.freeze(['bike_key', 'source']),
    }),
    'avt:audio_stage_completed': Object.freeze({
      required: Object.freeze(['bike_key', 'builder_stage', 'source']),
      fields: Object.freeze(['bike_key', 'builder_stage', 'source']),
    }),
    'avt:audio_close_match_accepted': Object.freeze({
      required: Object.freeze(['source', 'warning_count']),
      fields: Object.freeze(['source', 'warning_count']),
    }),
    'avt:audio_checkout_failed': Object.freeze({
      required: Object.freeze(['bike_key', 'source']),
      fields: Object.freeze(['bike_key', 'source']),
    }),
    'avt:audio_build_added': Object.freeze({
      required: Object.freeze(['bike_key', 'item_count', 'source', 'warning_count']),
      fields: Object.freeze(['bike_key', 'item_count', 'source', 'warning_count']),
    }),
    'avt:guide_action': Object.freeze({
      required: Object.freeze(['action', 'article_handle', 'bike_key']),
      fields: Object.freeze(['action', 'article_handle', 'bike_key']),
    }),
    'avt:project_intent_selected': Object.freeze({
      required: Object.freeze(['bike_key', 'intent', 'source']),
      fields: Object.freeze(['bike_key', 'intent', 'source']),
    }),
    'avt:project_started': Object.freeze({
      required: Object.freeze(['bike_key', 'intent', 'job_id', 'job_version', 'source']),
      fields: Object.freeze(['bike_key', 'intent', 'job_id', 'job_version', 'source']),
    }),
    'avt:project_plan_ready': Object.freeze({
      required: Object.freeze(['bike_key', 'job_id', 'job_version', 'source']),
      fields: Object.freeze([
        'bike_key',
        'blocking_gap_count',
        'job_id',
        'job_version',
        'source',
        'unresolved_role_count',
      ]),
    }),
    'avt:project_basket_added': Object.freeze({
      required: Object.freeze(['item_count', 'source']),
      fields: Object.freeze([
        'bike_key',
        'item_count',
        'job_id',
        'job_version',
        'product_id',
        'source',
        'variant_id',
      ]),
    }),
    'avt:service_handoff': Object.freeze({
      required: Object.freeze(['source', 'stage']),
      fields: Object.freeze(['bike_key', 'intent', 'job_id', 'request', 'source', 'stage']),
    }),
    'avt:project_completed': Object.freeze({
      required: Object.freeze(['bike_key', 'job_id', 'job_version', 'source']),
      fields: Object.freeze(['bike_key', 'job_id', 'job_version', 'source']),
    }),
  });

  let privacyReady = false;

  const clearAttribution = () => {
    try {
      window.sessionStorage.removeItem(attributionStorageKey);
    } catch {
      return;
    }
  };

  const customerPrivacy = () => {
    const privacy = window.Shopify?.customerPrivacy;
    return (
      privacy
      && typeof privacy.analyticsProcessingAllowed === 'function'
      && typeof privacy.marketingAllowed === 'function'
    )
      ? privacy
      : null;
  };

  const privacyAllows = (method) => {
    const privacy = customerPrivacy();
    if (!privacyReady || !privacy) return false;
    try {
      return privacy[method]() === true;
    } catch {
      return false;
    }
  };

  const sanitizedAttributionFrom = (input) => {
    if (!input || typeof input !== 'object' || Array.isArray(input)) return null;
    const attribution = {};
    Object.values(attributionParameters).forEach((field) => {
      const value = sanitizeAttribution(input[field]);
      if (value !== undefined) attribution[field] = value;
    });
    return Object.keys(attribution).length > 0 ? attribution : null;
  };

  const readAttribution = () => {
    try {
      const parsed = JSON.parse(window.sessionStorage.getItem(attributionStorageKey));
      const attribution = sanitizedAttributionFrom(parsed);
      if (!attribution) clearAttribution();
      return attribution;
    } catch {
      clearAttribution();
      return null;
    }
  };

  const captureAttribution = () => {
    if (!privacyAllows('marketingAllowed')) {
      clearAttribution();
      return;
    }
    const params = new URLSearchParams(window.location.search);
    const attribution = {};
    Object.entries(attributionParameters).forEach(([parameter, field]) => {
      const value = sanitizeAttribution(params.get(parameter));
      if (value !== undefined) attribution[field] = value;
    });
    if (Object.keys(attribution).length === 0) {
      readAttribution();
      return;
    }
    try {
      window.sessionStorage.setItem(
        attributionStorageKey,
        JSON.stringify(attribution),
      );
    } catch {
      return;
    }
  };

  const syncConsent = () => {
    if (!privacyReady) {
      clearAttribution();
      return;
    }
    if (privacyAllows('marketingAllowed')) captureAttribution();
    else clearAttribution();
  };

  const sanitizeEventData = (eventName, input) => {
    const schema = eventSchemas[eventName];
    if (!schema || !input || typeof input !== 'object' || Array.isArray(input)) {
      return null;
    }
    const data = {};
    schema.fields.forEach((field) => {
      const sanitize = fieldSanitizers[field];
      const value = sanitize?.(input[field]);
      if (value !== undefined && !Number.isNaN(value)) data[field] = value;
    });
    return schema.required.every((field) => Object.hasOwn(data, field))
      ? data
      : null;
  };

  const currentPath = () => {
    const path = String(window.location.pathname || '/');
    return (
      path.length <= 180
      && /^\/[A-Za-z0-9/_\-.%]*$/.test(path)
    )
      ? path
      : '/';
  };

  const currentTemplate = () => {
    const value = document.querySelector('main[data-template]')?.dataset.template;
    return sanitizeSlug(value, 80);
  };

  const publish = (eventName, input) => {
    const eventData = sanitizeEventData(eventName, input);
    if (!eventData || !privacyAllows('analyticsProcessingAllowed')) return false;
    const analytics = window.Shopify?.analytics;
    if (!analytics || typeof analytics.publish !== 'function') return false;

    const payload = {
      schema_version: 1,
      path: currentPath(),
    };
    const template = currentTemplate();
    if (template) payload.template = template;
    Object.assign(payload, eventData);

    if (privacyAllows('marketingAllowed')) {
      captureAttribution();
      const attribution = readAttribution();
      if (attribution) Object.assign(payload, attribution);
    } else {
      clearAttribution();
    }

    try {
      const result = analytics.publish(eventName, payload);
      if (result && typeof result.catch === 'function') result.catch(() => {});
      return true;
    } catch {
      return false;
    }
  };

  const acceptPrivacy = (error) => {
    if (error || !customerPrivacy()) {
      privacyReady = false;
      clearAttribution();
      return;
    }
    privacyReady = true;
    syncConsent();
  };

  document.addEventListener('visitorConsentCollected', () => {
    acceptPrivacy(customerPrivacy() ? null : new Error('privacy_unavailable'));
  });

  if (typeof window.Shopify?.loadFeatures === 'function') {
    try {
      window.Shopify.loadFeatures(
        [{ name: 'consent-tracking-api', version: '0.1' }],
        acceptPrivacy,
      );
    } catch (error) {
      acceptPrivacy(error);
    }
  } else if (customerPrivacy()) {
    acceptPrivacy(null);
  } else {
    acceptPrivacy(new Error('privacy_unavailable'));
  }

  Object.defineProperty(window, 'AVTJourney', {
    configurable: false,
    enumerable: true,
    writable: false,
    value: Object.freeze({
      publish,
      schemaVersion: 1,
    }),
  });
})();
